import React, { useCallback, useEffect, useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { useGamePublishers } from "@seabrookstudios/pitch2table";
import { PublisherName } from "../users/UserName";

const SIGNED_STATUS = "Signed";
const PublisherLabel = "Publisher";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {(gameId: import("@seabrookstudios/types").GameId, data: any[]) => void} props.onData
 * @param {(gameId: import("@seabrookstudios/types").GameId, isLoading: boolean) => void} props.onLoading
 */
const GamePublishersLoader = ({ gameId, onData, onLoading }) => {
  const { data = [], isLoading } = useGamePublishers(gameId);

  useEffect(() => {
    onData(gameId, data);
  }, [data, gameId, onData]);

  useEffect(() => {
    onLoading(gameId, isLoading);
  }, [gameId, isLoading, onLoading]);

  return null;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId[]} props.gameIds
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} props.value
 * @param {(value: import("@seabrookstudios/pitch2table-core").PublisherId[]) => void} props.onChange
 */
export const RoyaltyReminderPublisherSelect = ({ gameIds, value, onChange }) => {
  const [publisherGamesByGameId, setPublisherGamesByGameId] = useState(/** @type {Record<string, any[]>} */ ({}));
  const [publisherGamesLoadingByGameId, setPublisherGamesLoadingByGameId] = useState(
    /** @type {Record<string, boolean>} */ ({})
  );

  useEffect(() => {
    if (gameIds.length === 0) {
      setPublisherGamesByGameId({});
      setPublisherGamesLoadingByGameId({});
      return;
    }

    setPublisherGamesByGameId((current) => {
      const next = /** @type {Record<string, any[]>} */ ({});
      gameIds.forEach((gameId) => {
        if (current[gameId]) {
          next[gameId] = current[gameId];
        }
      });
      return next;
    });

    setPublisherGamesLoadingByGameId((current) => {
      const next = /** @type {Record<string, boolean>} */ ({});
      gameIds.forEach((gameId) => {
        if (current[gameId]) {
          next[gameId] = current[gameId];
        }
      });
      return next;
    });
  }, [gameIds]);

  /**
   * @param {import("@seabrookstudios/types").GameId} gameId
   * @param {any[]} data
   */
  const handlePublisherGames = useCallback((gameId, data) => {
    setPublisherGamesByGameId((current) => ({
      ...current,
      [gameId]: data || [],
    }));
  }, []);

  /**
   * @param {import("@seabrookstudios/types").GameId} gameId
   * @param {boolean} isLoading
   */
  const handleLoading = useCallback((gameId, isLoading) => {
    setPublisherGamesLoadingByGameId((current) => ({
      ...current,
      [gameId]: isLoading,
    }));
  }, []);

  const signedPublishers = useMemo(() => {
    const uniquePublishers = new Map();
    Object.values(publisherGamesByGameId).forEach((publisherGames) => {
      publisherGames.forEach((publisherGame) => {
        if (publisherGame?.status !== SIGNED_STATUS) {
          return;
        }
        if (!publisherGame?.publisherId) {
          return;
        }
        uniquePublishers.set(publisherGame.publisherId, publisherGame);
      });
    });
    return Array.from(uniquePublishers.values());
  }, [publisherGamesByGameId]);

  const availablePublisherIds = useMemo(
    () => signedPublishers.map((publisherGame) => publisherGame.publisherId),
    [signedPublishers]
  );

  const publisherGamesLoading =
    gameIds.length > 0 &&
    gameIds.some((gameId) => publisherGamesLoadingByGameId[gameId] || publisherGamesByGameId[gameId] == null);

  useEffect(() => {
    if (gameIds.length === 0) {
      if (value.length > 0) {
        onChange([]);
      }
      return;
    }
    if (publisherGamesLoading) {
      return;
    }
    const nextValue = value.filter((publisherId) => availablePublisherIds.includes(publisherId));
    if (nextValue.length !== value.length) {
      onChange(nextValue);
    }
  }, [availablePublisherIds, gameIds.length, publisherGamesLoading, onChange, value]);

  /**
   * @param {import("@mui/material").SelectChangeEvent<import("@seabrookstudios/pitch2table-core").PublisherId>} ev
   */
  const onPublisherChange = (ev) => {
    onChange([/** @type {import("@seabrookstudios/pitch2table-core").PublisherId} */ (ev.target.value)]);
  };

  const publisherHelperText =
    gameIds.length === 0
      ? "Select games to unlock publisher options."
      : "Publishers are limited to publisher records in a signed state.";

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId} publisherId
   */
  const renderPublisherValue = (publisherId) => {
    return <PublisherName publisherId={publisherId} />;
  };

  return (
    <>
      {gameIds.map((gameId) => (
        <GamePublishersLoader key={gameId} gameId={gameId} onData={handlePublisherGames} onLoading={handleLoading} />
      ))}
      <FormControl sx={{ width: "100%" }} margin="normal" disabled={gameIds.length === 0}>
        <InputLabel id="publisher-select">{PublisherLabel}</InputLabel>
        <Select
          labelId="publisher-select"
          value={value[0]}
          onChange={onPublisherChange}
          label={PublisherLabel}
          input={<OutlinedInput label={PublisherLabel} />}
          renderValue={renderPublisherValue}
        >
          {publisherGamesLoading ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : signedPublishers.length === 0 ? (
            <MenuItem disabled>No signed publishers for selected games.</MenuItem>
          ) : (
            signedPublishers.map((publisherGame) => (
              <MenuItem key={publisherGame.publisherId} value={publisherGame.publisherId}>
                <PublisherName publisherId={publisherGame.publisherId} />
              </MenuItem>
            ))
          )}
        </Select>
        <FormHelperText>{publisherHelperText}</FormHelperText>
      </FormControl>
    </>
  );
};
